import { useSignUp } from '@clerk/react'
import { zodResolver } from '@hookform/resolvers/zod'
import Input from 'components/input'
import { useShowSnack } from 'components/snackbar'
import { Controller, useForm } from 'react-hook-form'
import Button from 'components/button'
import { SizableText, YStack } from 'tamagui'
import { EmailVerificationSchema, type EmailVerificationFormData } from '../schemas'

interface Props {
    onVerified: () => void
}

export const EmailVerificationStep = ({ onVerified }: Props) => {
    const { signUp } = useSignUp()
    const showSnack = useShowSnack()

    const {
        control,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm<EmailVerificationFormData>({
        resolver: zodResolver(EmailVerificationSchema),
        defaultValues: { code: '' },
    })

    const onSubmit = async (data: EmailVerificationFormData) => {
        try {
            const verifyResult = await signUp.verifications.verifyEmailCode({
                code: data.code,
            })
            if (verifyResult.error) {
                showSnack(verifyResult.error.message ?? 'Invalid verification code', 'danger')
                return
            }
            if (signUp.status === 'complete') {
                const finalizeResult = await signUp.finalize()
                if (finalizeResult.error) {
                    showSnack(finalizeResult.error.message ?? 'Failed to complete sign up', 'danger')
                    return
                }
                onVerified()
            }
        } catch (err: any) {
            const message =
                err?.errors?.[0]?.longMessage ?? 'Invalid verification code'
            showSnack(message, 'danger')
        }
    }

    const handleResend = async () => {
        try {
            const result = await signUp.verifications.sendEmailCode()
            if (result.error) {
                showSnack(result.error.message ?? 'Failed to resend code', 'danger')
                return
            }
            showSnack('Verification code resent', 'success')
        } catch {
            showSnack('Failed to resend code', 'danger')
        }
    }

    return (
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <YStack gap="$4">
                <SizableText fontSize="$8" fontWeight="700" letterSpacing={-0.3}>
                    Check your email
                </SizableText>
                <SizableText color="$gray10" fontSize="$4">
                    We sent a 6-digit verification code to your email address
                </SizableText>

                <Controller
                    name="code"
                    control={control}
                    render={({ field }) => (
                        <Input
                            label="Verification Code"
                            editMode
                            placeholder="123456"
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            error={errors.code?.message}
                        />
                    )}
                />

                <Button
                    theme="primary"
                    // @ts-ignore - HTML form submit
                    type="submit"
                    loading={isSubmitting}
                    animation="quick"
                    hoverStyle={{ backgroundColor: '$backgroundHover' }}
                    pressStyle={{ backgroundColor: '$backgroundPress', opacity: 0.85 }}
                >
                    Verify Email
                </Button>

                <SizableText textAlign="center" color="$gray10" fontSize="$3">
                    Didn't receive the code?{' '}
                    <SizableText
                        color="$primary"
                        cursor="pointer"
                        onPress={handleResend}
                    >
                        Resend
                    </SizableText>
                </SizableText>
            </YStack>
        </form>
    )
}
